import { useEffect } from "react";
import { useMap } from "react-leaflet";

/**
 * Holds a screen wake lock for exactly as long as follow-me mode is on, so a driver's
 * phone does not dim out from under them while they are not touching it. See
 * travelmidwest-app#80.
 *
 * leaflet.locatecontrol fires locateactivate/locatedeactivate on the map, which gives
 * the lock the same lifetime as the feature the user deliberately switched on. It is
 * pointedly not held for the whole map page: the screen permanently on plus continuous
 * GPS is a real battery drain, and nobody asked for that just by opening a map.
 *
 * Takes the map rather than calling useMap itself so it can be tested against a plain
 * event emitter, without standing up a Leaflet map.
 */
export const useFollowMeWakeLock = (map) => {
  useEffect(() => {
    // Absent on iOS before 16.4, on older Android browsers, and on any insecure
    // origin. Follow-me still works there; the screen just dims as it always has.
    if (!map || !navigator.wakeLock) return undefined;

    let lock = null; // the sentinel currently held, if any
    let requesting = false; // a request is in flight
    let wanted = false; // follow-me is on
    let cancelled = false; // the effect has been torn down

    const releaseSentinel = (sentinel) => {
      try {
        // release() rejects if the OS got there first, which is routine, not an error.
        Promise.resolve(sentinel?.release?.()).catch(() => {});
      } catch {
        /* already released */
      }
    };

    const acquire = async () => {
      if (cancelled || !wanted || lock || requesting) return;
      // request() rejects outright on a hidden page, so do not even ask; the
      // visibilitychange listener will come back here when the page returns.
      if (document.visibilityState !== "visible") return;
      requesting = true;
      try {
        // Called synchronously here, so a request made from the control's own click
        // handler still runs inside that user gesture.
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled || !wanted) {
          // Follow-me was switched off, or the map unmounted, while the request was
          // in flight. Nobody wants this lock now.
          releaseSentinel(sentinel);
          return;
        }
        lock = sentinel;
        // The OS drops the lock whenever the page stops being visible. Clear our
        // handle when it does, so the next visibilitychange asks for a fresh one
        // instead of believing it still holds this one.
        sentinel.addEventListener?.("release", () => {
          if (lock === sentinel) lock = null;
        });
      } catch {
        // Refused -- battery saver, or the page stopped being visible mid-request.
        // Not worth telling the user about; follow-me is unaffected.
      } finally {
        requesting = false;
      }
    };

    const release = () => {
      const sentinel = lock;
      lock = null;
      if (sentinel) releaseSentinel(sentinel);
    };

    const activate = () => {
      wanted = true;
      acquire();
    };

    const deactivate = () => {
      wanted = false;
      release();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") acquire();
    };

    map.on("locateactivate", activate);
    map.on("locatedeactivate", deactivate);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      map.off("locateactivate", activate);
      map.off("locatedeactivate", deactivate);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // Leaving the map page drops the lock too, not just switching follow-me off.
      wanted = false;
      release();
    };
  }, [map]);
};

const FollowMeWakeLock = () => {
  const map = useMap();
  useFollowMeWakeLock(map);
  return null;
};

export default FollowMeWakeLock;
